/**
 * Supported phone brands and models.
 */
export const deviceOptions: Record<string, string[]> = {
  Apple: [
    "iPhone 16 Pro Max",
    "iPhone 16 Pro",
    "iPhone 16 Plus",
    "iPhone 16",
    "iPhone 16e",
    "iPhone 15 Pro Max",
    "iPhone 15 Pro",
    "iPhone 15 Plus",
    "iPhone 15",
    "iPhone 14 Pro Max",
    "iPhone 14 Pro",
    "iPhone 14 Plus",
    "iPhone 14",
    "iPhone 13 Pro Max",
    "iPhone 13 Pro",
    "iPhone 13",
    "iPhone 13 mini",
    "iPhone 12 Pro Max",
    "iPhone 12 Pro",
    "iPhone 12",
    "iPhone 12 mini",
    "iPhone SE (3rd generation, 2022)",
  ],
  Samsung: [
    "Galaxy S25 Ultra",
    "Galaxy S25+",
    "Galaxy S25",
    "Galaxy S24 Ultra",
    "Galaxy S24+",
    "Galaxy S24",
    "Galaxy S24 FE",
    "Galaxy S23 Ultra",
    "Galaxy S23+",
    "Galaxy S23",
    "Galaxy S23 FE",
    "Galaxy S22 Ultra",
    "Galaxy S22+",
    "Galaxy S22",
    "Galaxy S21 Ultra",
    "Galaxy S21+",
    "Galaxy S21",
    "Galaxy S21 FE",
    "Galaxy Z Fold7",
    "Galaxy Z Flip7",
    "Galaxy Z Flip7 FE",
    "Galaxy Z Fold6",
    "Galaxy Z Flip6",
    "Galaxy Z Fold5",
    "Galaxy Z Flip5",
    "Galaxy Z Fold4",
    "Galaxy Z Flip4",
    "Galaxy Z Fold3",
    "Galaxy Z Flip3",
  ],
  Google: [
    "Pixel 10 Pro Fold",
    "Pixel 10 Pro XL",
    "Pixel 10 Pro",
    "Pixel 10",
    "Pixel 9 Pro Fold",
    "Pixel 9 Pro XL",
    "Pixel 9 Pro",
    "Pixel 9",
    "Pixel 9a",
    "Pixel 8a",
    "Pixel 8 Pro",
    "Pixel 8",
    "Pixel Fold",
    "Pixel 7a",
    "Pixel 7 Pro",
    "Pixel 7",
    "Pixel 6a",
    "Pixel 6 Pro",
    "Pixel 6",
    "Pixel 5a (5G)",
  ],
};

export type DeviceOption = "byod" | "addon";

/** "Add Device" Placeholder pricing. */
export const ADDON_DEVICE = {
  brand: "Google",
  model: "Pixel 8",
  price: 300,
} as const;

export const ADDON_DEVICE_NAME = `${ADDON_DEVICE.brand} ${ADDON_DEVICE.model}`;

/** What the device fields collect. */
export interface DeviceSelection {
  option: DeviceOption;
  brand: string;
  deviceModel: string;
  caseLink: string;
}

export const EMPTY_DEVICE_SELECTION: DeviceSelection = {
  option: "byod",
  brand: "",
  deviceModel: "",
  caseLink: "",
};

/** Anything unrecognised is BYOD. */
export function normalizeDeviceOption(option?: string): DeviceOption {
  return option === "addon" ? "addon" : "byod";
}

/** The add-on charged on top of the kit price. */
export function deviceAddOnPrice(option?: string): number {
  return normalizeDeviceOption(option) === "addon" ? ADDON_DEVICE.price : 0;
}

export function isDeviceComplete(value: DeviceSelection): boolean {
  if (value.option === "addon") return true;
  return !!value.brand && !!value.deviceModel;
}

/**
 * The string persisted on a cart line, e.g. `"Apple iPhone 15 Pro"`.
 *
 * The format predates this module and both the backend cart record and older
 * saved carts already use it, so it is kept byte-for-byte.
 */
export function formatDevice(value: DeviceSelection): string {
  if (value.option === "addon") return ADDON_DEVICE_NAME;
  return `${value.brand} ${value.deviceModel}`;
}

/** The device fields a cart line stores, as written by both add and edit. */
export function deviceFieldsFor(value: DeviceSelection): {
  device: string;
  deviceOption: DeviceOption;
  caseLink: string | undefined;
} {
  const addon = value.option === "addon";
  return {
    device: formatDevice(value),
    deviceOption: value.option,
    caseLink: addon ? undefined : value.caseLink || undefined,
  };
}

/**
 * Recover a brand/model pair from a persisted device string.
 *
 * Used when the cart's inline editor opens on an existing line. Falls back to
 * an empty selection when the stored string predates the current model list.
 */
export function parseDevice(
  device?: string,
  caseLink?: string,
  deviceOption?: string
): DeviceSelection {
  const option = normalizeDeviceOption(deviceOption);
  if (option === "addon") {
    // No brand or model
    return { ...EMPTY_DEVICE_SELECTION, option, caseLink: "" };
  }

  const base = { ...EMPTY_DEVICE_SELECTION, option, caseLink: caseLink ?? "" };
  if (!device) return base;

  for (const [brand, models] of Object.entries(deviceOptions)) {
    const match = models.find(
      (model) => device === `${brand} ${model}` || device === model
    );
    if (match) return { ...base, brand, deviceModel: match };
  }
  return base;
}
